import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import type { RelationshipType, DataConsent } from '@/store/sessionTypes'
import { SessionTypeIcon } from '@/components/SessionTypeIcon'
import { SESSION_TYPE_OPTIONS, DATA_CONSENT_OPTIONS } from '@/store/clientFormOptions'
import { addClient, type NameIdentifierType } from '@/store/clients'
import styles from './AddClientPage.module.css'

const NAME_IDENTIFIER_OPTIONS: { id: NameIdentifierType; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'pseudonym', label: 'Pseudonym' },
  { id: 'reference', label: 'Reference code' },
]

export default function AddClientPage() {
  const { palette } = useTheme()
  const navigate      = useNavigate()

  const [name,        setName]        = useState('')
  const [nameIdentifierType, setNameIdentifierType] = useState<NameIdentifierType>('name')
  const [sessionType, setSessionType] = useState<RelationshipType | null>(null)
  const [dataConsent, setDataConsent] = useState<DataConsent | null>(null)

  const canSave =
    name.trim().length > 0 &&
    sessionType !== null &&
    dataConsent !== null

  function handleSave() {
    if (!canSave || sessionType === null || dataConsent === null) return

    addClient({
      name: name.trim(),
      nameIdentifierType,
      sessionType,
      track: 'practice',
      dataConsent,
    })
    navigate('/clients')
  }

  const paletteVars = {
    '--ac-bg':   palette.bg,
    '--ac-text': palette.text,
    '--ac-ring': palette.ring,
  } as React.CSSProperties

  return (
    <main className={styles.page} style={paletteVars} aria-label="Add a client">
      <div className={styles.inner}>
        <header className={styles.header}>
          <h1 className={styles.title}>Add a client</h1>
          <p className={styles.subtitle}>
            A few details to get started. You can change these later.
          </p>
        </header>

        <form
          className={styles.form}
          onSubmit={e => { e.preventDefault(); handleSave() }}
        >
          {/* Client name */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="client-name">
              Client name
            </label>
            <input
              id="client-name"
              type="text"
              className={styles.textInput}
              placeholder="Name, pseudonym or reference"
              value={name}
              onChange={e => setName(e.target.value)}
              autoComplete="off"
            />
            <div
              className={styles.identifierRadios}
              role="radiogroup"
              aria-label="Identifier type"
            >
              {NAME_IDENTIFIER_OPTIONS.map(opt => (
                <label key={opt.id} className={styles.identifierRadio}>
                  <input
                    type="radio"
                    name="name-identifier-type"
                    className={styles.identifierRadioInput}
                    value={opt.id}
                    checked={nameIdentifierType === opt.id}
                    onChange={() => setNameIdentifierType(opt.id)}
                  />
                  <span className={styles.identifierRadioLabel}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Session type */}
          <div className={styles.field}>
            <span className={styles.label} id="session-type-label">
              Session type
            </span>
            <div
              className={styles.cardGrid}
              role="group"
              aria-labelledby="session-type-label"
            >
              {SESSION_TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`${styles.optionCard} ${styles.optionCardInline}`}
                  data-selected={sessionType === opt.id}
                  aria-pressed={sessionType === opt.id}
                  onClick={() => setSessionType(opt.id)}
                >
                  <SessionTypeIcon type={opt.id} ring={palette.ring} className={styles.optionIcon} />
                  <span className={styles.optionLabel}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Data and privacy */}
          <div className={styles.field}>
            <span className={styles.label} id="data-privacy-label">
              Data and privacy
            </span>
            <div
              className={styles.cardGrid}
              role="group"
              aria-labelledby="data-privacy-label"
            >
              {DATA_CONSENT_OPTIONS.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={styles.optionCard}
                  data-selected={dataConsent === opt.id}
                  aria-pressed={dataConsent === opt.id}
                  onClick={() => setDataConsent(opt.id)}
                >
                  <span className={styles.optionLabel}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            className={styles.saveBtn}
            disabled={!canSave}
            aria-disabled={!canSave}
          >
            Save
          </button>
        </form>
      </div>
    </main>
  )
}
